import { AuthActionCreators, IGet } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { exportToXlsx, SheetConfig } from '@nkr/utils/exportToExcel';
import { useEffect, useState } from 'react';
import * as moment from 'moment';
import useCoachList from './use-coach-list';

interface IAssignmentsByCenterData {
    centerAlias: string;
    isCLM: boolean;
    month: string;
    newAssignments: number;
}

export interface ICenterMonthlyAssignments {
    centerAlias: string;
    isCLM: boolean;
    months: { [key: string]: number };
}

function pivot(rows: IAssignmentsByCenterData[]): ICenterMonthlyAssignments[] {
    const map = rows.reduce((acc, row) => {
        const key = moment(row.month).format('YYYY-MM');

        if (!acc.has(row.centerAlias)) {
            acc.set(row.centerAlias, { centerAlias: row.centerAlias, isCLM: row.isCLM, months: {} });
        }

        acc.get(row.centerAlias).months[key] = row.newAssignments;

        return acc;
    }, new Map<string, ICenterMonthlyAssignments>());

    return Array.from(map.values()).sort((a, b) => {
        if (a.isCLM !== b.isCLM) return a.isCLM ? -1 : 1;
        return a.centerAlias.localeCompare(b.centerAlias);
    });
}

export interface IMonthColumn {
    key: string;
    label: string;
}

export function buildMonthColumns(year: number): IMonthColumn[] {
    return Array.from({ length: 13 }, (_, i) => {
        const m = moment({ year: year - 1, month: moment().month() }).add(i, 'months');
        return { key: m.format('YYYY-MM'), label: m.format('MMM YYYY') };
    });
}

interface IRetVal {
    data: ICenterMonthlyAssignments[] | null;
    columns: IMonthColumn[];
    loading: boolean;
    error: string;
    exportData: () => void;
}

function useAssignmentsByCenter(isSuperAdmin: boolean, year: number, coachId: number, enabled = false): IRetVal {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<ICenterMonthlyAssignments[] | null>(null);
    const { getCoachName } = useCoachList();

    const columns = buildMonthColumns(year);

    useEffect(() => {
        if (!enabled) return;
        setLoading(true);
        setError('');

        const url = isSuperAdmin
            ? `${__API__}/admin/microsites/report/assignments-by-center?year=${year}&coachContactId=${coachId}`
            : `${__API__}/microsite/coach/report/assignments-by-center?year=${year}`;

        const config: IGet = { url };

        const fetchData = async () => {
            try {
                const response = await dispatch(AuthActionCreators.apiGet(config));

                if (response && response.data.success) {
                    setData(pivot(response.data.results));
                } else {
                    setError('Unable to load assignments by center');
                    setData(null);
                }
            } catch (err) {
                setData(null);
                setError(err?.message || 'An unexpected error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isSuperAdmin, year, coachId, enabled]);

    const exportData = (): void => {
        if (!data) return;

        const coachName = isSuperAdmin ? getCoachName(coachId) : null;

        const sheetCols = [
            { key: 'center', header: 'Center' },
            ...columns.map(c => ({ key: c.key, header: c.label })),
        ];

        const rows = data.map(center => {
            const row: Record<string, string | number> = { center: center.centerAlias };
            columns.forEach(c => { row[c.key] = center.months[c.key] ?? 0; });
            return row;
        });

        const sheet: SheetConfig = { name: 'Assignments by Center', columns: sheetCols, data: rows };
        exportToXlsx(`New Assignments by Center${coachName ? ` - ${coachName}` : ''} - ${year}.xlsx`, [sheet]);
    };

    return { data, columns, loading, error, exportData };
}

export default useAssignmentsByCenter;
